import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import content from "@/content";
import { scrollToSection } from "./Navbar";

const EASE = [0.22, 1, 0.36, 1];

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: EASE, delay: 0.35 + i * 0.14 },
  }),
};

export default function Hero() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 18 });
  const sy = useSpring(my, { stiffness: 40, damping: 18 });
  const imgX = useTransform(sx, [-0.5, 0.5], [-18, 18]);
  const imgY = useTransform(sy, [-0.5, 0.5], [-12, 12]);
  const secX = useTransform(sx, [-0.5, 0.5], [14, -14]);
  const secY = useTransform(sy, [-0.5, 0.5], [10, -10]);

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative min-h-screen flex items-center overflow-hidden texture-grain"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#F3E2D3]/70 via-linen to-linen" />
      <div aria-hidden="true" className="absolute -bottom-10 -left-4 font-serif italic text-[22vw] leading-none text-outline select-none pointer-events-none hidden sm:block">
        VM
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-24 w-full grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
            className="text-[11px] sm:text-xs uppercase tracking-[0.35em] text-crimson mb-8"
            data-testid="hero-eyebrow"
          >
            {content.hero.eyebrow}
          </motion.p>

          <h1 className="font-serif font-light text-5xl sm:text-7xl lg:text-[5.4rem] leading-[1.02] tracking-tight" data-testid="hero-heading">
            {content.hero.lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                >
                  {i === 1 ? <em className="text-crimson not-italic font-normal">{line}</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 1.1 }}
            className="mt-8 max-w-md text-sm sm:text-base text-slate2 leading-relaxed"
            data-testid="hero-intro"
          >
            {content.hero.intro}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 1.3 }}
            className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6"
          >
            <button
              data-testid="hero-cta-btn"
              onClick={() => scrollToSection("#enquire")}
              className="bg-crimson text-linen text-[11px] uppercase tracking-[0.25em] px-8 py-4 hover:bg-ink transition-colors duration-500 shadow-lg shadow-crimson/20"
            >
              Begin your enquiry
            </button>
            <button
              data-testid="hero-looks-btn"
              onClick={() => scrollToSection("#recent-looks")}
              className="text-[11px] uppercase tracking-[0.25em] text-ink hover:text-crimson transition-colors duration-300 underline underline-offset-8 decoration-gold whitespace-nowrap"
            >
              View recent looks
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: EASE, delay: 0.9 }}
          className="md:hidden relative mt-4"
        >
          <div className="absolute -inset-2 border border-gold/40 translate-x-3 translate-y-3" />
          <div className="relative overflow-hidden aspect-[4/5]">
            <img
              src={content.hero.image}
              alt="Editorial bridal couture"
              data-testid="hero-image-mobile"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>

        <div className="lg:col-span-5 relative hidden md:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.4, ease: EASE, delay: 0.5 }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-gold/40 translate-x-4 translate-y-4" />
            <motion.div style={{ x: imgX, y: imgY }} className="relative overflow-hidden aspect-[3/4]">
              <img
                src={content.hero.image}
                alt="Editorial bridal couture"
                data-testid="hero-image"
                className="w-full h-full object-cover scale-110"
              />
            </motion.div>
            <motion.div
              style={{ x: secX, y: secY }}
              className="absolute -bottom-10 -left-14 w-40 aspect-square overflow-hidden border-4 border-linen shadow-xl hidden lg:block"
            >
              <img
                src={content.hero.secondaryImage}
                alt="Jewellery and textile detail"
                data-testid="hero-secondary-image"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-0 inset-x-0 border-t border-gold/30 bg-linen/50 backdrop-blur-sm"
        data-testid="hero-bottom-bar"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-end text-[10px] uppercase tracking-[0.25em] text-slate2">
          <a
            href={content.brand.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-instagram-link"
            className="hover:text-crimson transition-colors duration-300"
          >
            {content.brand.instagram}
          </a>
        </div>
      </motion.div>
    </section>
  );
}
